<template>
  <div class="license-item">
    <v-card
      :class="`card-container ${darkMode ? 'dark-border' : 'light-border'} ${
        isDialog ? 'noRadius' : ''
      }`"
      flat
    >
      <div v-if="loading && !viewingAdmin" class="progress">
        <pulse-loader :color="loaderColor"></pulse-loader>
      </div>

      <v-layout v-else wrap style="max-width: 895px">
        <v-flex v-if="canViewAdmin && viewingAdmin" xs12>
          <v-switch
            :key="enabled"
            v-model="enabled"
            :color="`${$vuetify.theme.dark ? 'primary darken-1' : 'primary'}`"
            :label="enabled ? 'Enabled' : 'Disabled'"
            :disabled="isLoadingLicenseActivation"
            :loading="isLoadingLicenseActivation"
            @change="changeLicenseState"
          ></v-switch>
        </v-flex>

        <v-flex xs3 sm2>
          <v-img
            :src="image"
            :lazy-src="lazyImage"
            aspect-ratio="1"
            class="grey lighten-2 license-image"
            height="94px"
            width="94px"
          ></v-img>
        </v-flex>
        <v-flex xs9 sm7 class="pl-2">
          <div v-if="licenseName" class="text-h5 d-flex align-center">
            <span>{{ licenseName }}</span>
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-icon
                  dense
                  class="ml-2"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="openEditNameDialog"
                >
                  edit
                </v-icon>
              </template>
              <span>Edit license name</span>
            </v-tooltip>
          </div>
          <div class="detail-item body-2l text-subtitle-1">
            <!-- License ID: <span>{{ license.license.id }}</span> -->
            <span>
              {{ license.license.id }}
              <v-tooltip v-if="hasClipboard" top>
                <template #activator="{ on }">
                  <v-icon small @click="copyID(license.license.id)" v-on="on">
                    content_copy
                  </v-icon>
                </template>
                <span>Copy</span>
              </v-tooltip>
            </span>
          </div>
          <div
            v-if="!!license.license.authentication && !license.license.available"
            class="detail-item"
          >
            Connect Status: Bound
            <!-- {{ license.license.authentication.createdAt | utilityFormatDate }} -->
            {{ utilityFormatDate(license.license.authentication.createdAt) }}
          </div>
          <div
            v-if="hasApexBinding && apexBindingData && apexBindingData.updated_at"
            class="detail-item"
          >
            E6 Golf 2026 Status: Bound
            <!-- {{ apexBindingData.updated_at | utilityFormatDate }} -->
            {{ utilityFormatDate(apexBindingData.updated_at) }}
          </div>
          <div v-if="canViewAdmin && viewingAdmin && userEmail" class="detail-item">
            User:
            <a
              class="user-link"
              @click="routeToUser(licenseUserID)"
              @contextmenu.prevent="handleRightClick($event, license)"
            >
              {{ userEmail }}
            </a>
          </div>
          <div v-if="expirationTime" class="text-caption license-expiration primary--text">
            {{ expirationTime | utility_fromDate }}
          </div>
        </v-flex>
        <v-flex xs12 sm3 class="text-center text-sm-right">
          <div class="icon">
            <div v-if="isMobileLicense">
              <v-icon v-if="license.license.available" class="key">phone_iphone</v-icon>
              <v-icon
                v-if="!license.license.available && license.license.clientTrackingID"
                class="key blue--text"
              >
                phone_iphone
              </v-icon>

              <v-tooltip v-if="hasApexBinding" top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-if="hasApexBinding && apexBindingData && apexBindingData.id"
                    v-bind="attrs"
                    class="apex pl-1 tgGreen--text text--darken-2"
                    v-on="on"
                  >
                    api
                  </v-icon>
                </template>
                <span>Apex Bound</span>
              </v-tooltip>

              <v-tooltip v-if="expired" class="tooltip" top>
                <template #activator="{ on }">
                  <v-icon
                    v-if="
                      (!license.license.available && license.license.expiredLicense) ||
                      (license.license.expiredLicense && license.license.available)
                    "
                    class="key orange--text text--darken-2 pl-2"
                    v-on="on"
                  >
                    warning
                  </v-icon>
                </template>
                <span>Expired</span>
              </v-tooltip>
              <span class="pl-1 icon-text">Mobile</span>
            </div>

            <div v-else-if="isDesktopLicense">
              <v-icon v-if="license.license.available" class="key">vpn_key</v-icon>
              <v-tooltip
                v-else-if="!license.license.available && license.license.clientTrackingID"
                top
                class="key green--text text--darken-2"
              >
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-if="!license.license.available && license.license.clientTrackingID"
                    v-bind="attrs"
                    class="key green--text text--darken-2"
                    v-on="on"
                  >
                    vpn_key
                  </v-icon>
                </template>
                <span>E6 Connect Bound</span>
              </v-tooltip>

              <v-tooltip v-if="hasApexBinding" top>
                <template #activator="{ on, attrs }">
                  <v-icon
                    v-if="hasApexBinding && apexBindingData && apexBindingData.id"
                    v-bind="attrs"
                    class="apex pl-1 tgGreen--text"
                    v-on="on"
                  >
                    api
                  </v-icon>
                </template>
                <span>E6 Golf 2026 Bound</span>
              </v-tooltip>

              <v-tooltip v-if="expired" class="tooltip" top>
                <template #activator="{ on }">
                  <v-icon
                    v-if="
                      (!license.license.available && license.license.expiredLicense) ||
                      (license.license.expiredLicense && license.license.available)
                    "
                    class="key orange--text text--darken-2 pl-2"
                    v-on="on"
                  >
                    warning
                  </v-icon>
                </template>
                <span>Expired</span>
              </v-tooltip>
              <span class="pl-1 icon-text">Desktop</span>
            </div>

            <div v-else-if="isMultiSportLicense">
              <v-icon v-if="license.license.available" class="key">sports_baseball</v-icon>
              <v-icon
                v-if="!license.license.available && license.license.clientTrackingID"
                class="key green--text text--darken-2"
              >
                sports_baseball
              </v-icon>
              <v-tooltip v-if="expired" class="tooltip" top>
                <template #activator="{ on }">
                  <v-icon
                    v-if="
                      (!license.license.available && license.license.expiredLicense) ||
                      (license.license.expiredLicense && license.license.available)
                    "
                    class="key orange--text text--darken-2 pl-2"
                    v-on="on"
                  >
                    warning
                  </v-icon>
                </template>
                <span>Expired</span>
              </v-tooltip>
              <span class="pl-1 icon-text">MultiSport</span>
            </div>
          </div>
          <div
            v-if="
              !license.license.available &&
              license.license.clientTrackingID &&
              !!license.clientName &&
              license.clientName.length > 0
            "
            class="detail-item"
          >
            Computer Name: <br />{{ license.clientName | notAvailable }}
          </div>
          <div
            v-if="!license.license.available && license.license.clientTrackingID"
            class="detail-item"
          >
            Sim ID: {{ license.license.clientTrackingID | lastSix }}
          </div>

          <v-divider
            v-if="hasApexBinding && !license.license.available && license.license.clientTrackingID"
          ></v-divider>

          <div v-if="hasApexBinding" class="detail-item pt-1">
            E6 Golf 2026 Computer: <br />{{ apexBindingData.client_name | notAvailable }}
          </div>
        </v-flex>

        <!-- Original Connect only unbind =================== -->
        <!-- <v-flex xs12 sm12 md3 offset-md9 class="text-right" v-if="!isProductDetails">
          <v-btn block color="primary" @click="unbind(license)" v-if="!licenseAvailable">
            unbind
          </v-btn>
        </v-flex> -->

        <!-- E6 Golf 2026 and Connect unbind -->
        <v-flex v-if="!isProductDetails" xs12 sm12 md3 offset-md9 class="text-right">
          <v-btn
            v-if="!licenseAvailable || hasApexBinding"
            block
            color="primary"
            @click="unbindLicenseInit(license)"
          >
            unbind
          </v-btn>
        </v-flex>

        <v-flex xs12 class="inactive-switch">
          <v-switch
            v-model="showInactive"
            dense
            hide-details
            color="primary"
            :label="showInactive ? 'Hide Inactive Products' : 'Show Inactive Products'"
            :disabled="isLoadingLicenseActivation"
            :loading="isLoadingLicenseActivation"
          ></v-switch>
        </v-flex>

        <!-- Expansion panel for products -->
        <v-flex xs12 class="py-0">
          <v-expansion-panels
            flat
            accordion
            class="products py-0"
            :value="hasTransactions || showInactive ? 0 : 1"
          >
            <v-expansion-panel>
              <v-expansion-panel-header class="product-details px-0">
                <div class="py-0">License Products</div>
                <template #actions>
                  <v-icon :color="$vuetify.theme.dark ? '#ffffff' : 'info'">
                    $vuetify.icons.expand
                  </v-icon>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-card flat>
                  <LicenseItemProduct
                    :integration-transactions="integrationTransactions"
                    :license="license"
                    :can-view-admin="canViewAdmin"
                    :show-inactive="showInactive"
                    type="integration"
                    @deactivated="deactivateProduct"
                  />
                  <LicenseItemProduct
                    :subscription-transactions="subscriptionTransactions"
                    :license="license"
                    :can-view-admin="canViewAdmin"
                    :show-inactive="showInactive"
                    :is-admin="isAdmin"
                    type="subscription"
                    @cancelled="subscriptionCancel"
                    @deactivated="deactivateProduct"
                  />
                  <LicenseItemProduct
                    :purchase-transactions="purchaseTransactions"
                    :license="license"
                    :can-view-admin="canViewAdmin"
                    :show-inactive="showInactive"
                    type="purchase"
                    @deactivated="deactivateProduct"
                  />
                  <LicenseItemProduct
                    :consumable-transactions="consumableTransactions"
                    :license="license"
                    :can-view-admin="canViewAdmin"
                    :show-inactive="showInactive"
                    type="consumable"
                    @deactivated="deactivateProduct"
                  />
                </v-card>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-flex>

        <LicenseItemAuthorizations
          :license="license"
          :viewing-admin="viewingAdmin"
          :can-view-admin="canViewAdmin && viewingAdmin"
        />

        <!-- Expansion panel for apex binding -->
        <v-flex xs12 class="py-0">
          <v-expansion-panels
            v-if="localLicense && apexBindingData && apexBindingData.id"
            flat
            accordion
            class="products py-0"
          >
            <v-expansion-panel>
              <v-expansion-panel-header class="product-details px-0">
                <div class="py-0">E6 Golf 2026 Binding</div>
                <template #actions>
                  <v-icon :color="$vuetify.theme.dark ? '#ffffff' : 'info'">
                    $vuetify.icons.expand
                  </v-icon>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-card flat>
                  <v-card-text>
                    <v-layout wrap>
                      <v-flex xs12 sm7 md8 text-left>
                        <b>Computer Name:</b> {{ apexBindingData.client_name }}
                      </v-flex>
                      <v-flex xs12 sm5 md4 text-left text-sm-right>
                        <v-btn
                          small
                          :color="`${$vuetify.theme.dark ? 'primary lighten-1' : 'primary'}`"
                          class="mr-0"
                          @click="apexUnbind(apexBindingData, localLicense)"
                        >
                          unbind
                        </v-btn>
                      </v-flex>
                    </v-layout>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-flex>
      </v-layout>
    </v-card>

    <!-- future setup for using dialog -->
    <!-- <v-dialog v-model="dialog" transition="dialog-bottom-transition" width="500">
      <v-card :dark="$vuetify.theme.dark ? true : false">
        <v-card-title class="text-h5 primary white--text" primary-title> CAUTION! </v-card-title>

        <v-card-text class="text-body-1 py-6 font-weight-bold">
          Are you sure you want to enable/disable this license?
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click.stop="dialog = false"> cancel </v-btn>
          <v-btn color="primary" text @click.stop="confirmEnableDisable"> ok </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog> -->

    <!-- Edit License Name dialog -->
    <v-dialog v-model="editNameDialog" width="500" @click:outside="closeEditNameDialog">
      <v-card :dark="$vuetify.theme.dark ? true : false">
        <v-card-title class="text-h5 text-uppercase primary white--text" primary-title>
          Edit License Name
        </v-card-title>

        <v-card-text class="py-6">
          <v-text-field
            v-model="editedName"
            :disabled="isSavingName"
            label="Custom License Name"
            hide-details="auto"
            counter="100"
            autofocus
            clearable
            :rules="[(v) => (v && v.trim().length > 0) || 'Name is required']"
          />
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text :disabled="isSavingName" @click="closeEditNameDialog">
            cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="isSavingName"
            :disabled="!canSaveEditedName || isSavingName"
            text
            @click.stop="saveEditedName"
          >
            save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Unbinding choice dialog -->
    <v-dialog
      v-show="bindingDialog"
      v-model="bindingDialog"
      width="500"
      @click:outside="bindingDialog = false"
    >
      <v-card :dark="$vuetify.theme.dark ? true : false">
        <v-card-title class="text-h5 text-uppercase primary white--text" primary-title>
          {{ $t('store.general.attention') }}
        </v-card-title>

        <v-card-text class="text-body-1 py-6 font-weight-bold">
          Would you like to unbind
          {{
            hasApexBinding && hasConnectBinding
              ? 'E6 Golf 2026 or E6 CONNECT'
              : hasApexBinding
              ? 'E6 Golf 2026'
              : hasMultiSportBinding
              ? 'MultiSport ARCADE'
              : 'E6 CONNECT'
          }}?
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click="bindingDialog = false">
            {{ $t('general.no') }}
          </v-btn>
          <v-btn
            v-if="hasConnectBinding || hasMultiSportBinding"
            color="primary"
            text
            @click.stop="unbindConnectOrMSConfirm"
          >
            {{
              !hasApexBinding ? 'YES' : hasMultiSportBinding ? 'MultiSport ARCADE' : 'E6 CONNECT'
            }}
          </v-btn>
          <v-btn v-if="hasApexBinding" color="primary" text @click.stop="unbindApexConfirm">
            {{ !hasConnectBinding ? 'YES' : 'E6 Golf 2026' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ContextMenu ref="menu">
      <template slot-scope="{ contextData }">
        <ContextMenuItem @click.native="viewUserNewTab(contextData)">
          View in new tab
        </ContextMenuItem>
      </template>
    </ContextMenu>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import groupBy from 'lodash/groupBy';
import { isBefore, parseISO } from 'date-fns';
import utilityMixin from '@/mixins/utilityMixin';
import { utilityFormatDate } from '@/utils/filters';

import LicenseItemProduct from '@/components/settings/LicenseItemProduct';
import LicenseItemAuthorizations from '@/components/settings/LicenseItemAuthorizations';
import ContextMenu from '@/components/context-menu/ContextMenu';
import ContextMenuItem from '@/components/context-menu/ContextMenuItem';

export default {
  name: 'LicenseItem',

  components: {
    LicenseItemProduct,
    LicenseItemAuthorizations,
    ContextMenu,
    ContextMenuItem,
  },

  filters: {
    uppercase: (s) => {
      if (s === undefined || s === '') return s;
      return s.toUpperCase();
    },

    lastSix: (s) => {
      if (s === undefined) return '';
      return s.substr(s.length - 6);
    },

    notAvailable: (s) => {
      if (s === undefined || s === '') return 'N/A';
      return s;
    },

    // dateUpdate: d => {
    //   if (d === '' || d === undefined) return;
    //   return moment(d).format('MMM Do YYYY, h:mm:ss a');
    // },

    // returnTime: t => {
    //   if (t === '' || t === undefined) return;
    //   return moment(t, 'X').format('h:mm:ss');
    // },
  },

  mixins: [utilityMixin],

  props: {
    license: {
      type: Object,
      default: () => {
        return {};
      },
    },
    licenseType: {
      type: String,
      default: '',
    },
    expired: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    userEmail: {
      type: String,
      default: '',
    },
    isDialog: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      image: 'https://e6golf-qa-assets.imgix.net/uploads/clubhouses/branding/e6Connect_logo_1.jpg',
      lazyImage:
        'https://e6golf-qa-assets.imgix.net/uploads/clubhouses/branding/e6Connect_logo_1.jpg',
      tabItems: [
        { title: 'Permissions', key: 'permissions' },
        { title: 'Courses', key: 'courses' },
        { title: 'Tracking Systems', key: 'trackingSystems' },
      ],
      active: null,
      loaderColor: '#BA2127',
      transactions: [],
      disabled: false,
      enabled: true,
      purchaseType: 'purchase',
      consumableType: 'consumable',
      excludedLicenseProducts: ['MOBLIC'],
      showInactive: false,
      dialog: false,
      localLicense: this.license,
      bindingDialog: false,
      licenseToUnbind: null,
      editNameDialog: false,
      editedName: '',
      isSavingName: false,
    };
  },

  computed: {
    ...mapGetters({
      // showTransactions: 'transactions/show',
      canViewAdmin: 'users/canViewAdmin',
      isLoadingLicenseActivation: 'admin/isLoadingLicenseActivation',
    }),

    isMobileLicense() {
      return this.license?.type?.toLowerCase() === 'mobile' || false;
    },

    isDesktopLicense() {
      return this.license?.type?.toLowerCase() === 'desktop' || false;
    },

    isMultiSportLicense() {
      return this.license?.type?.toLowerCase() === 'multi-sport' || false;
    },

    isProductDetails() {
      return this.$route.name === 'productDetails';
    },

    subscriptionTransactions() {
      if (!this.transactions.length) {
        return [];
      }

      // if (this.transactions) {
      //* Return all subscriptions
      // return this.transactions.filter(
      //   product =>
      //     // return product.productType.type === 'subscription';
      //     product.productType === 'subscription'
      // );

      //* Filter out duplicate redeemed subscriptions
      const subscriptions = this.transactions //* original
        // return this.showTransactions //* Looks to get overridden each time
        .filter((transaction) => {
          return (
            (transaction.productType === 'subscription' ||
              transaction.productCategory === 'licenseSubscription') &&
            transaction.licenseID === this.license?.license?.id
          );
        })
        .sort((a, b) => {
          return new Date(b.redeemedAt) - new Date(a.redeemedAt);
        })
        .reduce((acc, current, _, arr) => {
          //* group same transaction IDs
          // const grouped = arr.reduce((r, a) => {
          //   r[a.originalTransactionID] = [...(r[a.originalTransactionID] || []), a];
          //   return r;
          // }, {});

          //* Using lodash
          const grouped = groupBy(arr, 'originalTransactionID');

          //* Pull out latest transaction
          const x = acc.some(
            (item) =>
              //* This does work to remove those with product keys as ids
              //* But does it not work for other scenarios
              {
                return item.originalTransactionID === current.originalTransactionID;
              }
            //* Not sure why I originally checked for the product code
            //* This removes those types of products...not sure the purpose
            // || this.checkForProductCode(current.originalTransactionID)
          );
          if (!x) {
            //* if grouped is only 1, it is the first purchase and not renewed
            if (grouped[current.originalTransactionID]?.length <= 1) {
              current.initialPurchase = true;
            }
            current.latestTransaction = true;
            return acc.concat([current]);
          }

          //* added due to WEB-695 issue
          return acc.concat([current]);
          // return acc;
        }, []);

      if (!subscriptions.length) {
        return subscriptions ?? [];
      }

      // console.log('subscriptions:', subscriptions.length > 0 ? subscriptions : []);
      const countedSubscriptions = this.findOccurrences(subscriptions);
      return countedSubscriptions.length < subscriptions.length
        ? countedSubscriptions
        : subscriptions ?? [];
    },

    purchaseTransactions() {
      if (!this.transactions.length) {
        return [];
      }

      const purchases = this.transactions.filter((product) => {
        return (
          product.licenseID === this.license.license.id &&
          (this.consumableCourseContent(product) ||
            (product.productType === 'purchase' &&
              product.productCategory !== 'trackingSystem' &&
              product.productCategory !== 'licenseSubscription' &&
              product.productCategory === 'courseContent'))
        );
      });

      //* Disabling grouped purchases as it's unlikely to happen now. Possible future case.
      // const countedPurchases = this.findOccurrences(purchases);
      // return countedPurchases.length < purchases.length ? countedPurchases : purchases ?? [];

      return purchases.length ? purchases ?? [] : [];
    },

    consumableTransactions() {
      if (!this.transactions.length) {
        return [];
      }

      const consumables = this.transactions.filter((product) => {
        return (
          (this.consumableEventContent(product) ||
            this.consumableMopContent(product) ||
            this.purchaseMultiSportContent(product) ||
            this.purchaseEventContent(product) ||
            this.purchaseMopContent(product)) &&
          product.licenseID === this.license.license.id
        );
      });

      if (!consumables.length) {
        return consumables ?? [];
      }

      const countedConsumables = this.findOccurrences(consumables);
      return countedConsumables.length < consumables.length
        ? countedConsumables
        : consumables ?? [];
    },

    integrationTransactions() {
      if (!this.transactions.length) {
        return [];
      }

      const integrations = this.transactions.filter((product) => {
        return (
          product.licenseID === this.license.license.id &&
          !this.excludedLicenseProducts.includes(product.skuCode) &&
          (this.consumableTSContent(product) ||
            (product.productType === 'purchase' && product.productCategory === 'trackingSystem'))
        );
      });

      if (!integrations.length) {
        return integrations ?? [];
      }

      const countedIntegrations = this.findOccurrences(integrations);
      return countedIntegrations.length < integrations.length
        ? countedIntegrations
        : integrations ?? [];
    },

    hasTransactions() {
      return (
        this.subscriptionTransactions?.length ||
        this.purchaseTransactions?.length ||
        this.consumableTransactions?.length ||
        this.integrationTransactions?.length
      );
    },

    expirationTime() {
      if (this.license?.license?.expiresAt) {
        return this.license?.license?.expiresAt;
      }
      return null;
    },

    licenseName() {
      const name =
        this.localLicense?.license?.name ||
        this.license?.license?.name ||
        this.license?.license_key_name;
      return name ? name.toUpperCase() : name;
    },

    viewingAdmin() {
      return this.$route.fullPath.includes('admin');
    },

    // TODO do we need this?
    // hasLicenseClientName() {
    //   if (this.license) {
    //     console.log('this.license:', this.license);
    //     console.log('!!this.license.clientName:', !!this.license?.clientName);
    //     console.log(
    //       '!this.license.license.clientName:',
    //       !!this.license?.license?.authentication?.clientName
    //     );
    //     return !!this.license.clientName || !!this.license?.license?.authentication?.clientName;
    //   }
    //   return false;
    // },

    // TODO do we need this?
    // licenseClientName() {
    //   if (this.license) {
    //     return this.hasLicenseClientName
    //       ? this.license?.clientName || this.license?.license?.authentication?.clientName
    //       : '';
    //   }
    //   return '';
    // },

    licenseAvailable() {
      return this.license?.license?.available;
    },

    hasClipboard() {
      return !!navigator.clipboard;
    },

    licenseUserID() {
      if (this.utility_isObject(this.license)) {
        const { userID = '' } = this.license?.license;
        return userID;
      }
      return null;
    },

    apexBindingData() {
      if (!this.localLicense?.apexBinding?.id) {
        return null;
      }
      const { apexBinding = {} } = this.localLicense;
      return apexBinding;

      //* Delete later
      // if (!this.localLicense?.apexBindings?.length) {
      //   return null;
      // }
      // const { apexBindings = [] } = this.localLicense;
      // return apexBindings;
    },

    hasApexBinding() {
      return this.localLicense && !!this.apexBindingData?.id;
    },

    hasConnectBinding() {
      if (this.licenseToUnbind?.license?.authentication?.id) {
        return true;
      }
      return false;
    },

    hasMultiSportBinding() {
      if (
        this.licenseToUnbind?.license?.authentication?.id &&
        this.licenseToUnbind?.license?.license_key === 'multi_sport_core'
      ) {
        return true;
      }
      return false;
    },

    canSaveEditedName() {
      return !!(this.editedName && this.editedName.trim().length > 0);
    },
  },

  watch: {
    license(val) {
      if (val?.id) {
        this.fetchTransactions();
      }
    },
  },

  mounted() {
    this.fetchTransactions();
    this.disabled = !!this.license?.license?.disabledAt ?? false;
    this.enabled = !this.license?.license?.disabledAt ?? true;

    //* get redeemed products for each license
    // const { id } = this.license.license;
    // console.log('id:', id);
    // if (id) {
    //   this.$store.dispatch('transactions/show', id).then(res => {
    //     console.log('res:', res);
    //     if (res) {
    //       this.transactions = [...res];
    //       // res.forEach(item => {
    //       //   // this.transactions.push(item);
    //       //   this.transactions = [...item];
    //       // });
    //     }
    //   });
    // }
  },

  methods: {
    utilityFormatDate,

    checkUserUUID(id) {
      const pattern =
        /^[A-Za-z0-9]{8}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{12}$/;
      return pattern.test(id);
    },

    checkForProductCode(id) {
      return id.length === 23;
    },

    snackbarValues(status = 'true', type = 'success', text) {
      // console.log('status:', status);
      this.snackbar.status = status;
      this.snackbar.type = type;
      this.snackbar.text = text;
    },

    unbindLicenseInit(license) {
      this.licenseToUnbind = license;
      this.bindingDialog = true;
    },

    unbindConnectOrMSConfirm() {
      this.unbind(this.licenseToUnbind);
    },

    unbindApexConfirm() {
      this.apexUnbind(this.apexBindingData, this.localLicense);
    },

    unbind(sim, skipConfirm = false) {
      // console.log('unbind sim:', sim);
      // console.log('unbind skipConfirm:', skipConfirm);
      // if (skipConfirm || confirm('Are you sure you want to unbind this license?')) {
      if (
        sim?.license?.authentication?.clientOS === 'ios' ||
        sim?.license?.authentication?.clientOS === 'android'
      ) {
        // console.log('mobile')
        this.$store
          .dispatch('licenses/deleteMobile', sim.license.id)
          .then((res) => {
            this.$emit('snackbarStatus', {
              sim,
              isMobile: true,
              status: undefined,
              type: undefined,
              text: `License was successfully unbound from the associated mobile device ${
                skipConfirm ? 'and disabled' : ''
              }`,
            });

            //* Reset Binding Info
            this.licenseToUnbind = null;
            this.bindingDialog = false;

            this.$store
              .dispatch('licenses/showMobile', { licenseID: sim?.license?.id })
              .then(() => {
                //* repopulate user license list for admin
                if (res.userID && this.$route.path.includes('admin')) {
                  const isUUID = this.checkUserUUID(res.userID);
                  const payload = {
                    data: res.userID,
                    email: res.userID,
                    isUUID,
                  };
                  this.$store.dispatch('admin/getUserLicenses', payload);
                }
              });
          })
          .catch((err) => {
            console.error(err);
            //* Reset Binding Info
            this.licenseToUnbind = null;
            this.bindingDialog = false;
            this.$emit('snackbarStatus', {
              status: undefined,
              type: 'err',
              text: 'Unable to unbind license from the mobile device',
            });
          });
        sim.active = false;
      } else {
        // console.log('windows')
        this.$store
          .dispatch('licenses/delete', sim.license.id)
          .then((res) => {
            this.$emit('snackbarStatus', {
              sim,
              isDesktop: true,
              status: undefined,
              type: undefined,
              text: `License was successfully unbound from the associated device ${
                skipConfirm ? 'and disabled' : ''
              }`,
            });

            //* Reset Binding Info
            this.licenseToUnbind = null;
            this.bindingDialog = false;

            this.$store.dispatch('licenses/show', { licenseID: sim?.license?.id }).then((res) => {
              console.log('res:', res);
              //* repopulate user license list for admin
              if (res?.userID && this.$route.path.includes('admin')) {
                const isUUID = this.checkUserUUID(res?.userID);
                const payload = {
                  data: res.userID,
                  email: res.userID,
                  isUUID,
                };
                this.$store.dispatch('admin/getUserLicenses', payload);
              }
            });
          })
          .catch((err) => {
            console.error(err);
            //* Reset Binding Info
            this.licenseToUnbind = null;
            this.bindingDialog = false;
            this.$emit('snackbarStatus', {
              status: undefined,
              type: 'err',
              text: 'Unable to unbind license from the associated simulator',
            });
          });
        sim.active = false;
      }
      // }
    },

    apexUnbind(binding, license) {
      // console.log('apexUnbind binding:', binding);
      // console.log('apexUnbind license:', license);
      if (!binding?.id) {
        alert('Binding information missing. Please refresh and try again.');
        return;
      }
      // if (confirm('Are you sure you want to unbind this Apex license?')) {
      const { type } = license;
      const dispatchURL =
        type?.toLowerCase() === 'desktop' ? 'licenses/show' : 'licenses/showMobile';
      this.$store
        .dispatch('licenses/apexBindingDisable', {
          id: binding.id,
          unbind_source: 'web_manage_licenses',
        })
        .then(() => {
          this.$root.vtoast.show({
            text: `Successfully Unbound Apex license`,
            color: 'success',
          });

          //* Reset Binding Info
          this.licenseToUnbind = null;
          this.bindingDialog = false;

          this.$store
            .dispatch(dispatchURL, {
              licenseID: binding?.license_id,
              isAdmin: this.canViewAdmin && this.viewingAdmin,
            })
            .then((res) => {
              this.localLicense = { ...res };
              //* emit to update possibly

              //* repopulate user license list for admin
              if (res?.userID && this.$route.path.includes('admin')) {
                const isUUID = this.checkUserUUID(res?.userID);
                const payload = {
                  data: res.userID,
                  isUUID,
                };
                this.$store.dispatch('admin/getUserLicenses', payload);
              }
            });
        })
        .catch((err) => {
          console.error('err:', err);
          //* Reset Binding Info
          this.licenseToUnbind = null;
          this.bindingDialog = false;
          this.$root.vtoast.show({
            text: `Error unbinding Apex license`,
            color: 'error',
          });
        });
      // }
    },

    subscriptionCancel(payload) {
      this.$store
        .dispatch('shop/cancelSubscription', payload)
        .then((res) => {
          // console.log('res:', res);
          this.$store
            .dispatch('transactions/show', { id: payload.licenseID })
            .then((res) => {
              if (res) {
                this.transactions = [...res];
              }
              this.$emit('snackbarStatus', {
                status: undefined,
                type: undefined,
                text: 'Subscription was successfully cancelled',
              });
            })
            .catch((err) => {
              this.$emit('snackbarStatus', {
                status: undefined,
                type: 'err',
                text: 'Unable to cancel subscription',
              });
            });
        })
        .catch((err) => {
          console.error('err:', err);
          this.$emit('snackbarStatus', {
            status: undefined,
            type: 'err',
            text: 'Failed to cancel subscription',
          });
        });
    },

    deactivateProduct(payload) {
      this.$store
        .dispatch('admin/deactivateLicenseProduct', payload)
        .then(() => {
          if (payload?.subscriptionID !== '') {
            const stripeID = payload?.subscriptionID?.includes('sub_')
              ? payload.subscriptionID
              : payload?.transactionID?.includes('sub_')
              ? payload.transactionID
              : null;

            if (!stripeID) {
              this.$root.vtoast.show({
                text: `Unable to cancel Stripe subscription. No Stripe ID found. Please double-check this.`,
                color: 'Error',
                timeout: 10000,
              });
            } else {
              //* cancel the subscription too.
              const cancelPayload = {
                stripeSubscriptionID: stripeID,
                licenseID: payload?.licenseID,
              };
              this.subscriptionCancel(cancelPayload);
            }
          }
          this.$store
            .dispatch('transactions/show', { id: payload.licenseID })
            .then((res) => {
              if (res) {
                this.transactions = [...res];
              }
              this.$emit('snackbarStatus', {
                status: undefined,
                type: undefined,
                text: 'Product was successfully deactivated',
              });
            })
            .catch((err) => {
              this.$emit('snackbarStatus', {
                status: undefined,
                type: 'err',
                text: 'Unable to deactivate product',
              });
            });
        })
        .catch((err) => {
          console.error('err:', err);
          const { error } = err?.data;
          this.$root.vtoast.show({
            text: `Error ${error?.code}: ${error?.msg || 'Unable to deactivate product'}`,
            color: 'error',
          });
        });
    },

    fetchTransactions() {
      //* get redeemed products for each license
      const { id } = this.license?.license;
      if (id) {
        this.$store
          .dispatch('transactions/show', { id })
          .then((res) => {
            if (!res.length) {
              this.transactions = [];
            } else {
              const transactionArr = res?.filter((item) => {
                return item.licenseID === id;
              });
              // this.transactions = [...res];
              this.transactions = [...transactionArr];
              // res.forEach(item => {
              //   // this.transactions.push(item);
              //   this.transactions = [...item];
              // });
            }
          })
          .catch((error) => {
            console.error(error);
          });
      }
    },

    checkAndCancelSubscriptions(subs = []) {
      subs.forEach((sub) => {
        const { originalTransactionID, licenseID, transactionID, webTitle, subscriptionStatus } =
          sub;

        if (
          subscriptionStatus !== 'cancelled' &&
          (!originalTransactionID?.startsWith('trugolf_admin') ||
            originalTransactionID?.startsWith('sub_') ||
            transactionID?.startsWith('sub_'))
        ) {
          const payload = {
            stripeSubscriptionID: originalTransactionID?.startsWith('sub')
              ? originalTransactionID
              : transactionID,
            title: webTitle,
            licenseID,
          };

          this.subscriptionCancel(payload);
        }
      });
    },

    changeLicenseState(e) {
      const { available, id: licenseID } = this.license?.license;
      if (!licenseID) {
        alert('No license found');
        return;
      }
      const payload = { licenseID };
      const dispatchURL = e ? 'admin/activateLicense' : 'admin/deactivateLicense';

      const answer = confirm(
        `Are you sure you want to ${e ? 'enable' : 'disable'} this license? ${
          !available && !e ? 'This will also unbind the license from associated device.' : ''
        } ${!e ? 'Any active subscriptions will also be cancelled.' : ''}`
      );
      if (answer) {
        this.$store
          .dispatch(dispatchURL, payload)
          .then(() => {
            if (!available && !e) {
              this.unbind(this.license, true);
            }

            //* Cancel subscriptions here
            if (!e && this.subscriptionTransactions?.length) {
              this.checkAndCancelSubscriptions(this.subscriptionTransactions);
            }
            this.$emit('changedState', true);
          })
          .catch((error) => {
            console.error('error:', error);
            const { data } = error;
            this.$root.vtoast.show({
              text: `Error ${data?.code ?? ''}: ${data?.error ?? 'Unexpected error'}`,
              color: 'error',
            });
            setTimeout(() => {
              this.enabled = true;
            }, 100);
          });
        return;
      }
      setTimeout(() => {
        this.enabled = !this.enabled;
      }, 100);
    },

    consumableCourseContent(product) {
      const { productType, productCategory } = product;
      return productType === this.consumableType && productCategory === 'courseContent';
    },

    consumableTSContent(product) {
      const { productType, productCategory } = product;
      return productType === this.consumableType && productCategory === 'trackingSystem';
    },

    consumableMopContent(product) {
      const { productType, productCategory } = product;
      return productType === this.consumableType && productCategory === 'mopContent';
    },

    purchaseMultiSportContent(product) {
      const { productType, productCategory } = product;
      return productType === this.purchaseType && productCategory === 'multiSport';
    },

    consumableEventContent(product) {
      const { productType, productCategory } = product;
      return productType === this.consumableType && productCategory === 'eventContent';
    },

    purchaseCourseContent(product) {
      const { productType, productCategory } = product;
      return productType === this.purchaseType && productCategory === 'courseContent';
    },

    purchaseMopContent(product) {
      const { productType, productCategory } = product;
      return productType === this.purchaseType && productCategory === 'mopContent';
    },

    purchaseEventContent(product) {
      const { productType, productCategory } = product;
      return productType === this.purchaseType && productCategory === 'eventContent';
    },

    //* Possibly use this generic function for checking
    contentTypeCheck(product, type, category) {
      const { productType, productCategory } = product;
      return productType === type && productCategory === category;
    },

    findOccurrences(arr = []) {
      let res = [];
      const foundDeactivated = [];
      arr
        .sort((a, b) => {
          const dateA = new Date(a?.redeemedAt);
          const dateB = new Date(b?.redeemedAt);
          return dateB - dateA;
        })
        .forEach((el) => {
          const { expirationDate } = el;
          const isExpired = isBefore(parseISO(expirationDate), new Date());
          const isCancelled = !!el?.cancelledAt;
          const isDeactivated = !!el?.deactivatedAt;

          const index = res?.findIndex((obj) => {
            return obj.skuCode === el.skuCode;
          });

          if (index === -1) {
            if (!isCancelled && !isExpired && !isDeactivated) {
              res.push({
                ...el,
                count: 1,
              });
            } else if (isCancelled && !isExpired) {
              //* added due to WEB-695 issue - remove if issues arrive
              res.push({
                ...el,
                count: 1,
              });
            } else {
              foundDeactivated.push({
                ...el,
                count: 1,
              });
            }
          } else if (!isDeactivated) {
            res[index].count++;
          }

          // TODO Check if we want to remove count for these types so quantity doesn't increase
          // else {
          //   // if (isCancelled) {
          //   //   console.log('is cancelled', el);
          //   // }
          //   // if (isExpired) {
          //   //   console.log('is expired', el);
          //   // }
          //   // if (isDeactivated) {
          //   //   console.log('is deactivated', el);
          //   // }
          //   // if (!isCancelled && !isExpired && !isDeactivated) {
          //   //   res[index].count++;
          //   // }
          //   if (!isDeactivated) {
          //     res[index].count++;
          //   }
          // }

          // original just else { res[index].count++ }
          // else {
          //   res[index].count++;
          // }
        });

      const hasMultipleSomeDeactivated = res.some((item) => {
        return foundDeactivated.some((data) => {
          return data.skuCode === item.skuCode;
        });
      });

      // if (!hasMultipleSomeDeactivated && res.length && foundDeactivated.length) {
      //* this shows transactions if ALL of them are deactivated instead of hiding them
      if (!hasMultipleSomeDeactivated && foundDeactivated.length) {
        const merged = [];
        foundDeactivated
          //* Sort to show oldest transaction record first
          //* The oldest record should be the last to have been deactivated
          .sort((a, b) => {
            // return a.recordID - b.recordID;
            return b.recordID - a.recordID; //* puts newest first
          })
          .forEach((el) => {
            // const { expirationDate } = el;
            // const isExpired = isBefore(parseISO(expirationDate), new Date());
            const index = merged?.findIndex((obj) => {
              return obj.skuCode === el.skuCode;
            });
            if (index === -1) {
              //* Disabling expiration check to use show/hide inactive products
              // if (!isExpired) {
              merged.push({
                ...el,
                count: 1,
              });
              // }
            } else {
              merged[index].count++;
            }
          });
        res = [...res, ...merged];
      }

      return res;
    },

    copyID(text) {
      if (navigator?.clipboard) {
        navigator?.clipboard?.writeText(text).then(
          () => {
            console.log('Copying to clipboard was successful!');
          },
          (err) => {
            console.error('Could not copy text: ', err);
          }
        );
      } else {
        alert('Cannot copy to clipboard');
      }
    },

    handleRightClick(e, id) {
      this.$refs.menu.open(e, id);
    },

    viewUserNewTab(data) {
      const { userID = '' } = data?.license;
      this.$refs.menu.close();
      if (userID) {
        const routeData = this.$router.resolve({
          name: 'AdminUser',
          props: true,
          params: {
            id: userID,
          },
        });
        window.open(routeData.href, '_blank');
      }
      // console.log('no userID');
    },

    routeToUser(data) {
      this.$router
        .push({
          name: 'AdminUser',
          params: {
            id: data,
          },
        })
        .catch(() => {});
    },

    openEditNameDialog() {
      const currentName = this.localLicense?.license?.name || this.license?.license?.name || '';
      this.editedName = currentName;
      this.editNameDialog = true;
    },

    closeEditNameDialog() {
      this.editNameDialog = false;
      this.isSavingName = false;
    },

    async saveEditedName() {
      if (!this.canSaveEditedName) return;
      const licenseID = this.license?.license?.id;
      if (!licenseID) {
        this.$root.vtoast.show({ text: 'No license found.', color: 'error' });
        return;
      }

      this.isSavingName = true;

      try {
        await this.$store.dispatch('licenses/licenseNameUpdate', {
          id: licenseID,
          name: this.editedName.trim(),
        });

        // After updating the name, refresh the full license lists (page depends on combined lists)
        await this.$store.dispatch('licenses/all');

        this.$emit('changedState', true);
        this.$root.vtoast.show({ text: 'License name updated.', color: 'success' });
        this.closeEditNameDialog();
      } catch (error) {
        console.error('error:', error);
        const { data } = error || {};
        this.$root.vtoast.show({
          text: `Error ${data?.error?.code ?? ''}: ${data?.error?.msg ?? 'Unable to update name'}`,
          color: 'error',
        });
        this.isSavingName = false;
      }
    },
  },
};
</script>

<style scoped>
.license-item .card-container {
  padding: 30px !important;
  margin-bottom: 20px;
  border-radius: 10px;
  border-radius: var(--border-10);
  min-height: 5vh;
}

.license-item .card-container.noRadius {
  border-radius: var(--border-0);
}

.detail-item {
  font-size: 12px;
}

.detail-value {
  font-weight: 500;
}

.licenses,
.products {
  box-shadow: none;
}

.license-item >>> .v-expansion-panel__header {
  padding: 8px;
}

.license-title {
  font-size: 18px;
}

/* .tab-card {
  background-color: rgba(0, 0, 0, 0) !important;
  min-height: 80px;
} */

.icon-text {
  display: inline-block;
}

.key,
.apex {
  cursor: default;
}

/* ul {
  list-style: none;
  padding: 0;
  columns: 3;
  -webkit-columns: 3;
  -moz-columns: 3;
} */

.progress {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}

.inactive-switch {
  max-height: 50px;
}

.user-link:hover {
  text-decoration: underline;
}

@media (max-width: 1024px) {
  .license-image {
    width: 74px !important;
    height: 74px !important;
  }
}

@media (max-width: 960px) {
  .license-image {
    width: 54px !important;
    height: 54px !important;
  }
}

@media (max-width: 600px) {
  .license-image {
    width: 44px !important;
    height: 44px !important;
  }

  /* ul {
    columns: 2;
    -webkit-columns: 2;
    -moz-columns: 2;
  } */
}

.license-item * >>> .v-expansion-panel-content__wrap {
  padding-left: 8px;
  padding-right: 8px;
}
* >>> .license-details > .v-expansion-panel-header {
  padding: 0 !important;
}
</style>
